import React, {useState, useMemo, useCallback, memo} from 'react';
import {DndContext, closestCenter, useSensor, useSensors, PointerSensor} from '@dnd-kit/core';
import {arrayMove, SortableContext, useSortable, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {FaIcon} from "../FaIcon";
import {GET_COLUMN_DATA} from "./columns";
import { withTranslation } from 'react-i18next';

const SortableColumnItem = memo(({id, getColumnData, t, onRemove, activeId, overId}) => {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging: itemIsDragging} = useSortable({id});

    // Memoizar estilos para evitar recálculos innecesarios
    const style = useMemo(() => ({
        transform: CSS.Transform.toString(transform),
        transition: itemIsDragging ? 'none' : transition, // Deshabilitar transiciones durante drag
        opacity: itemIsDragging ? 0.5 : 1,
    }), [transform, transition, itemIsDragging]);

    // Solo calcular si es necesario (cuando hay un drag activo)
    const showDropIndicator = useMemo(() => {
        return activeId && overId === id && !itemIsDragging;
    }, [activeId, overId, id, itemIsDragging]);

    // Memoizar datos de columna
    const columnData = useMemo(() => getColumnData(id), [id, getColumnData]);

    const handleMouseEnter = useCallback((e) => {
        const tooltip = e.currentTarget.querySelector('.field-help-tooltip');
        if (tooltip) {
            const rect = e.currentTarget.getBoundingClientRect();
            tooltip.style.display = 'block';
            tooltip.style.position = 'fixed';
            tooltip.style.top = `${rect.bottom + 5}px`;
            tooltip.style.left = `${rect.left + rect.width / 2}px`;
            tooltip.style.transform = 'translateX(-50%)';
            tooltip.style.zIndex = '100000';
        }
    }, []);

    const handleMouseLeave = useCallback((e) => {
        const tooltip = e.currentTarget.querySelector('.field-help-tooltip');
        if (tooltip) {
            tooltip.style.display = 'none';
        }
    }, []);

    const handleRemoveClick = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onRemove) {
            onRemove(id);
        }
    }, [id, onRemove]);

    // Memoizar el icono de ayuda
    const helpIcon = useMemo(() => {
        if (!columnData) return null;
        const helpText = columnData.descriptionKey || columnData.helper;
        const hasHelp = columnData.hasDescription || (columnData.helper && columnData.helper.trim() !== '');

        if (!hasHelp || !helpText) return null;

        return (
            <span
                className="field-help-icon-container"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <FaIcon iconName="question-circle" className="field-help-icon" />
                <span className="field-help-tooltip">
                    <span className="tooltip-description">{helpText}</span>
                </span>
            </span>
        );
    }, [columnData, handleMouseEnter, handleMouseLeave]);

    // Memoizar className para evitar recálculos
    const className = useMemo(() => {
        let classes = 'sortable-item';
        if (itemIsDragging) classes += ' is-dragging';
        if (showDropIndicator) classes += ' drop-target';
        return classes;
    }, [itemIsDragging, showDropIndicator]);

    // Memoizar estilos inline del contenedor
    const containerStyle = useMemo(() => ({
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        cursor: itemIsDragging ? 'grabbing' : 'grab',
        minWidth: 0
    }), [itemIsDragging]);

    const iconColor = useMemo(() => itemIsDragging ? '#FF470F' : '#6c757d', [itemIsDragging]);

    // Si la columna no es reconocida, no renderizar
    if (!columnData) return null;

    return (
        <>
            <li 
                ref={setNodeRef} 
                style={style} 
                {...attributes} 
                className={className}
            >
                <div {...listeners} style={containerStyle}>
                    <FaIcon iconName="ellipsis-v" style={{ flexShrink: 0, color: iconColor }}/>
                    <div className="sortable-item-info" style={{ minWidth: 0, flex: 1 }}>
                        <p className="sortable-item-title">
                            {columnData.title}
                            {helpIcon}
                        </p>
                        {columnData.subtitle && <p className="sortable-item-subtitle">{columnData.subtitle}</p>}
                    </div>
                </div>
                {onRemove && (
                    <button 
                        type="button"
                        className="sortable-item-remove"
                        onClick={handleRemoveClick}
                        title={t('config_columns_modal.remove_column')}
                    >
                        <FaIcon iconName="times"/>
                    </button>
                )}
            </li>
        </>
    );
});

SortableColumnItem.displayName = 'SortableColumnItem';

const SortableColumnItemWithTranslation = memo(withTranslation()(({id, getColumnData, onRemove, activeId, overId, ...props}) => {
    const { t } = props;
    return <SortableColumnItem id={id} getColumnData={getColumnData} t={t} onRemove={onRemove} activeId={activeId} overId={overId} />;
}));

SortableColumnItemWithTranslation.displayName = 'SortableColumnItemWithTranslation';

export const ActiveColumnsList = ({activeColumns, onColumnsPositionChange, getColumnData, onColumnRemove}) => {
    const [activeId, setActiveId] = useState(null);
    const [overId, setOverId] = useState(null);

    // Sensors deben estar en el nivel superior (no se pueden memoizar hooks)
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const handleDragStart = useCallback((event) => {
        setActiveId(event.active.id);
    }, []);

    // Throttle handleDragOver para mejorar rendimiento
    const handleDragOver = useCallback((event) => {
        const newOverId = event.over?.id || null;
        // Solo actualizar si cambió el overId para evitar re-renders innecesarios
        setOverId(prev => prev !== newOverId ? newOverId : prev);
    }, []);

    const handleDragEnd = useCallback((event) => {
        const {active, over} = event;
        setActiveId(null);
        setOverId(null);

        if (active.id !== over?.id) {
            const oldIndex = activeColumns.indexOf(active.id);
            const newIndex = activeColumns.indexOf(over.id);
            if (oldIndex !== -1 && newIndex !== -1) {
                onColumnsPositionChange(arrayMove(activeColumns, oldIndex, newIndex));
            }
        }
    }, [activeColumns, onColumnsPositionChange]);

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
        setOverId(null);
    }, []);

    // Memoizar dataGetter
    const dataGetter = useMemo(() => getColumnData || GET_COLUMN_DATA, [getColumnData]);

    return (
        <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter} 
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <SortableContext items={activeColumns} strategy={verticalListSortingStrategy}>
                <ul className="sortable-list">
                    {activeColumns.map((columnName) => (
                        <SortableColumnItemWithTranslation 
                            key={columnName} 
                            id={columnName} 
                            getColumnData={dataGetter}
                            onRemove={onColumnRemove}
                            activeId={activeId}
                            overId={overId}
                        />
                    ))}
                </ul>
            </SortableContext>
        </DndContext>
    );
};
